import { memo, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';

import { useTrackClickedEllipsisMenuButton } from '@air/analytics';
import { type PublicClip } from '@air/api';
import { EllipsisIcon } from '@air/design-icons';
import { itemIsSelectedSelector } from '@air/redux-selected-items';

import { CardAction } from '~/components/CardAction';
import { useGetContentCollectionGalleryAssetMenuOptions } from '~/components/ContentCollection/hooks/useGetContentCollectionGalleryAssetMenuOptions';
import { CardEllipsisMenu } from '~/components/Gallery/CardEllipsisMenu';
import { useAirSelector } from '~/utils/ReduxUtils';

export type ContentCollectionGalleryAssetMenuProps = {
  clip: PublicClip;
  setIsMenuOpen: (value: boolean) => void;
};

export const ContentCollectionGalleryAssetMenu = memo(
  ({ clip, setIsMenuOpen }: ContentCollectionGalleryAssetMenuProps) => {
    const { trackClickedEllipsisMenuButton } = useTrackClickedEllipsisMenuButton();
    const { getContentCollectionAssetMenuOptions } = useGetContentCollectionGalleryAssetMenuOptions({ clip });

    const isSelected = useAirSelector((st) => itemIsSelectedSelector(st, clip.id));

    const selectionMenuOptions = getContentCollectionAssetMenuOptions();
    const ellipsisMenuOptions = getContentCollectionAssetMenuOptions();

    const onMenuChange = useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          trackClickedEllipsisMenuButton({ location: 'asset-item' });
        }

        flushSync(() => {
          setIsMenuOpen(isOpen);
        });
      },
      [setIsMenuOpen, trackClickedEllipsisMenuButton],
    );

    const menuProps = useMemo(
      () => ({
        options: isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions,
      }),
      [ellipsisMenuOptions, isSelected, selectionMenuOptions],
    );

    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (!menuProps?.options?.length) {
      return null;
    }

    return (
      <CardEllipsisMenu
        title="More asset options"
        menuProps={menuProps}
        onMenuChange={onMenuChange}
        actionSheetTestId="CONTENT_COLLECTION_ASSET_CARD_SUB_MENU"
        trigger={
          <CardAction data-testid="CONTENT_COLLECTION_ASSET_CARD_SUB_MENU_BUTTON" icon={<EllipsisIcon size="sm" />} />
        }
      />
    );
  },
);

ContentCollectionGalleryAssetMenu.displayName = 'ContentCollectionGalleryAssetMenu';
